import { Box, Link as MuiLink, Stack, Typography } from '@mui/material'
import { getClimateHazardMethodology } from '../climateAnalytics/common/hazardConfig'

type MethodologyTOCProps = {
  hazardCode: string
  /** Currently active anchor; highlighted in the list. */
  activeAnchor?: string
  /** Called with the section id when an entry is clicked (drawer variant uses this to scroll within the panel). */
  onSelect?: (anchor: string) => void
  /**
   * When provided, entries render as router links (`<a href={hrefBase}#id>`).
   * Otherwise entries render as buttons that only trigger `onSelect`.
   */
  hrefBase?: string
  variant?: 'drawer' | 'page'
}

/**
 * Compact table of contents driven by `HAZARD_META[hazard].methodology.sections`.
 * Used both in the drawer (scrolls within the drawer body) and on the standalone
 * page (anchors update the URL hash so deep links remain shareable).
 */
export default function MethodologyTOC({
  hazardCode,
  activeAnchor,
  onSelect,
  hrefBase,
  variant = 'page',
}: MethodologyTOCProps) {
  const methodology = getClimateHazardMethodology(hazardCode)
  if (!methodology || methodology.sections.length === 0) return null

  return (
    <Box
      component="nav"
      aria-label="Methodology sections"
      sx={{
        borderLeft: variant === 'page' ? '1px solid' : 'none',
        borderColor: 'divider',
        pl: variant === 'page' ? 2 : 0,
      }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        On this page
      </Typography>
      <Stack spacing={0.5}>
        {methodology.sections.map(section => {
          const active = activeAnchor === section.id
          const commonSx = {
            display: 'block',
            fontSize: 13,
            lineHeight: 1.4,
            color: active ? 'primary.main' : 'text.secondary',
            fontWeight: active ? 700 : 500,
            textDecoration: 'none',
            cursor: 'pointer',
            py: 0.5,
            px: 1,
            borderRadius: 1,
            '&:hover': { backgroundColor: 'action.hover', color: 'primary.main' },
          } as const
          if (hrefBase != null) {
            return (
              <MuiLink
                key={section.id}
                href={`${hrefBase}#${section.id}`}
                onClick={event => {
                  if (onSelect) {
                    event.preventDefault()
                    onSelect(section.id)
                  }
                }}
                sx={commonSx}>
                {section.label}
              </MuiLink>
            )
          }
          return (
            <Box
              key={section.id}
              component="button"
              type="button"
              onClick={() => onSelect?.(section.id)}
              sx={{ ...commonSx, border: 'none', background: 'transparent', textAlign: 'left', width: '100%' }}>
              {section.label}
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
